import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { apiUrl, mediaUrl } from '../utils/api';


function ServiceGallery({ service }) {
  const images = (service.images || []).slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const closeLightbox = () => setLightboxOpen(false);
  const move = (direction) => setActiveIndex((index) => (index + direction + images.length) % images.length);
  // move is stable for the gallery instance; the effect only activates while the lightbox is open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') closeLightbox(); if (event.key === 'ArrowLeft') move(-1); if (event.key === 'ArrowRight') move(1); };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('lightbox-open');
    return () => { document.removeEventListener('keydown', onKeyDown); document.body.classList.remove('lightbox-open'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);
  if (!images.length) return <div className="service-gallery service-gallery-empty"><span>No portfolio photos yet</span></div>;
  const current = images[activeIndex % images.length];
  return <>
    <div className="service-gallery">
      <button type="button" className="gallery-image-button" aria-label={`Enlarge ${service.name} image`} onClick={() => setLightboxOpen(true)}><img src={mediaUrl(current.url)} alt={current.caption || `${service.name} portfolio example`} /></button>
      <span className="gallery-expand-hint">Click to enlarge</span>
      {images.length > 1 && <>
        <button type="button" className="gallery-arrow gallery-arrow-prev" aria-label="Previous service image" onClick={() => move(-1)}>‹</button>
        <button type="button" className="gallery-arrow gallery-arrow-next" aria-label="Next service image" onClick={() => move(1)}>›</button>
        <div className="gallery-dots">{images.map((image, index) => <button type="button" key={image._id} className={index === activeIndex ? 'gallery-dot active' : 'gallery-dot'} aria-label={`Show image ${index + 1}`} onClick={() => setActiveIndex(index)} />)}</div>
      </>}
    </div>
    {lightboxOpen && <div className="gallery-lightbox" role="dialog" aria-modal="true" aria-label={`${service.name} gallery`} onMouseDown={(event) => { if (event.target === event.currentTarget) closeLightbox(); }}>
      <button type="button" className="lightbox-close" aria-label="Close gallery" onClick={closeLightbox}>×</button>
      {images.length > 1 && <button type="button" className="lightbox-arrow lightbox-prev" aria-label="Previous full-size image" onClick={() => move(-1)}>‹</button>}
      <figure className="lightbox-figure"><img src={mediaUrl(current.url)} alt={current.caption || `${service.name} portfolio example`} /><figcaption>{current.caption || `${service.name} · image ${activeIndex + 1} of ${images.length}`}</figcaption></figure>
      {images.length > 1 && <button type="button" className="lightbox-arrow lightbox-next" aria-label="Next full-size image" onClick={() => move(1)}>›</button>}
      {images.length > 1 && <div className="lightbox-thumbnails">{images.map((image, index) => <button type="button" key={image._id} className={index === activeIndex ? 'lightbox-thumb active' : 'lightbox-thumb'} onClick={() => setActiveIndex(index)}><img src={mediaUrl(image.url)} alt={`Thumbnail ${index + 1}`} /></button>)}</div>}
    </div>}
  </>;
}
const businessDayLabels = [
  ['monday', 'Monday'], ['tuesday', 'Tuesday'], ['wednesday', 'Wednesday'],
  ['thursday', 'Thursday'], ['friday', 'Friday'], ['saturday', 'Saturday'], ['sunday', 'Sunday'],
];
function formatBusinessTime(value) {
  const [hourText, minuteText] = String(value || '').split(':');
  const hour = Number(hourText);
  if (!Number.isFinite(hour)) return value || '—';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${minuteText || '00'} ${suffix}`;
}
function BusinessHours({ hours }) {
  return <section className="business-hours panel" aria-labelledby="business-hours-heading"><div className="business-hours-heading"><div><p className="eyebrow">Plan your visit</p><h2 id="business-hours-heading">Opening hours</h2></div><span className="business-hours-icon" aria-hidden="true">◷</span></div><div className="business-hours-grid">{businessDayLabels.map(([key, label]) => { const day = hours?.[key]; return <div className="business-hours-row" key={key}><strong>{label}</strong><span className={day?.closed ? 'business-hours-closed' : ''}>{day?.closed ? 'Closed' : `${formatBusinessTime(day?.open)} – ${formatBusinessTime(day?.close)}`}</span></div>; })}</div></section>;
}

const bookingPath = (businessSlug, serviceId = '') => { const target = `/appointment?business=${encodeURIComponent(businessSlug)}${serviceId ? `&serviceId=${encodeURIComponent(serviceId)}` : ''}`; const user = JSON.parse(localStorage.getItem('userInfo') || 'null'); return user?.token ? target : `/login?returnTo=${encodeURIComponent(target)}`; };
function BusinessProfile() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ rating: 0, reviewCount: 0 });
  const [error, setError] = useState('');
  const [canCustomerBook, setCanCustomerBook] = useState(() => {
    const info = JSON.parse(localStorage.getItem('userInfo') || 'null');
    const roles = info?.businessRoles?.map((membership) => membership.role) || [];
    return !info || info.role === 'admin' || (!['owner', 'manager', 'staff', 'professional'].includes(info.role) && !roles.some((role) => ['owner', 'manager', 'staff'].includes(role)));
  });
  useEffect(() => {
    const info = JSON.parse(localStorage.getItem('userInfo') || 'null');
    if (!info?.token) return undefined;
    let cancelled = false;
    axios.get(apiUrl('/api/auth/profile'), { headers: { Authorization: `Bearer ${info.token}` } }).then(({ data }) => {
      if (cancelled) return;
      const roles = data.businessRoles?.map((membership) => membership.role) || [];
      const businessUser = ['owner', 'manager', 'staff', 'professional'].includes(data.role) || roles.some((role) => ['owner', 'manager', 'staff'].includes(role));
      const refreshed = { ...info, ...data, token: info.token };
      localStorage.setItem('userInfo', JSON.stringify(refreshed));
      setCanCustomerBook(!businessUser || data.role === 'admin');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(apiUrl(`/api/businesses/${slug}`)),
      axios.get(apiUrl(`/api/businesses/${slug}/services`)),
      axios.get(apiUrl(`/api/businesses/${slug}/staff`)),
      axios.get(apiUrl(`/api/businesses/${slug}/reviews`)),
    ]).then(([businessRes, servicesRes, staffRes, reviewsRes]) => {
      setBusiness(businessRes.data);
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setReviews(reviewsRes.data.reviews || []);
      setReviewSummary(reviewsRes.data.summary || { rating: 0, reviewCount: 0 });
    }).catch(() => setError('Could not load this business profile.'));
  }, [slug]);

  if (error) return <div className="page"><p className="error-message">{error}</p></div>;
  if (!business) return <div className="page"><div className="panel">Loading business...</div></div>;

  return (
    <div className="page">
      <div className="profile-navigation"><Link className="button ghost" to="/">Back to Marketplace</Link></div>
      <section className="business-profile-hero panel">
        <div className="business-profile-copy">
          <p className="eyebrow">{business.type}</p>
          <h1>{business.name}</h1>
          <p className="hero-text">{business.description}</p>
          <p className="muted-text">📍 {business.address || business.city}, {business.country} · ⭐ {Number(reviewSummary.rating || business.rating || 0).toFixed(1)} from {reviewSummary.reviewCount || 0} verified reviews</p>
          {canCustomerBook && <Link className="button primary" to={bookingPath(business.slug)}>Book with {business.name}</Link>}
        </div>
        <div className="business-profile-media">
          <img src={business.heroImage || '/images/profile-hero.svg'} alt={`${business.name} beauty studio`} />
        </div>
      </section>
      <BusinessHours hours={business.businessHours} />
      {business.cancellationPolicy && <section className="cancellation-policy panel"><p className="eyebrow">Before you book</p><h2>Cancellation policy</h2><p>{business.cancellationPolicy}</p></section>}
      <section className="section-header"><p className="eyebrow">Services</p><h2>Choose a service</h2></section>
      <section className="service-grid">
        {services.map((service) => (
          <article className="service-card" key={service._id}>
            <ServiceGallery service={service} />
            <h3>{service.name}</h3><p>{service.category} · {service.durationMinutes} min</p><strong>${service.price}</strong>
            {canCustomerBook && <Link className="button ghost full" to={bookingPath(business.slug, service._id)}>Book this</Link>}
          </article>
        ))}
      </section>
      <section className="section-header"><p className="eyebrow">Team</p><h2>Professionals</h2></section>
      <section className="business-grid small">
        {staff.map((person) => <article className="service-card professional-marketplace-card" key={person._id}>{person.imageUrl ? <img className="professional-marketplace-image" src={mediaUrl(person.imageUrl)} alt={person.displayName} /> : <div className="professional-marketplace-placeholder">{String(person.displayName || 'P').slice(0, 1).toUpperCase()}</div>}<div className="professional-marketplace-info"><h3>{person.displayName}</h3><p>{person.title}</p>{person.bio && <p className="professional-bio">{person.bio}</p>}</div></article>)}
      </section>
      <section className="section-header"><p className="eyebrow">Verified experiences</p><h2>What customers are saying</h2></section>
      <section className="review-grid">
        {reviews.length === 0 && <div className="panel"><p className="muted-text">No verified reviews yet. Be the first to share your experience after your appointment.</p></div>}
        {reviews.map((review) => <article className="panel review-card" key={review._id}>
          <div className="review-card-head"><div><strong>{review.customer?.name || 'Verified customer'}</strong><span className="verified-review-badge">✓ Verified booking</span></div><span className="review-stars" aria-label={`${review.rating} out of 5 stars`}>{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</span></div>
          {review.title && <h3>{review.title}</h3>}
          <p>{review.comment}</p>
          <small className="muted-text">{review.service}{review.staffName ? ` · ${review.staffName}` : ''}</small>
          {review.response?.body && <div className="business-review-response"><strong>{review.response.respondedBy?.name || 'Business response'}</strong><p>{review.response.body}</p></div>}
        </article>)}
      </section>
    </div>
  );
}

export default BusinessProfile;
